import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  CONVERSATION_CREDIT_NOTE_MAX_LENGTH,
  CONVERSATION_CREDIT_REASONS,
  type ConversationCreditReason,
} from "@sage-mcp/shared";
import {
  api,
  ApiError,
  type AdminConversationDetail,
  type AdminConversationMessage,
  type AdminMessageContentPart,
  type AdminMessageFeedback,
  type AdminMessageRole,
} from "../api.js";
import { useAuth, useToken } from "../auth.js";
import { RequestPricing, formatRecordedUsd } from "../components/RequestPricing.js";
import { Badge, type Tone } from "../components/Badge.js";
import { CREDIT_REASON_LABELS } from "../credit-reason-labels.js";
import { FEEDBACK_REASON_LABELS } from "../feedback-labels.js";
import { formatCost } from "./Conversations.js";

export function ConversationDetail() {
  const { id = "" } = useParams();
  const token = useToken();
  const { user, emergency } = useAuth();
  const { t, i18n } = useLingui();
  // Crediting is superuser-only. The masking is cosmetic — the cloud refuses the
  // POST with a 403 either way — but it matches what the API accepts, so no
  // hidden surprises. Same idiom as CustomerDetail's canPauseCustomer.
  const canCredit = emergency || user?.role === "superuser";
  const [detail, setDetail] = useState<AdminConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [creditOpen, setCreditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoadError(null);
    try {
      const r = await api.conversationDetail(token, id);
      setDetail(r.conversation);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : String(err));
    }
  }, [token, id]);

  useEffect(() => {
    setLoading(true);
    void refresh().finally(() => setLoading(false));
  }, [refresh]);

  const uncredit = useCallback(async () => {
    if (!detail) return;
    if (!confirm(t`Remove the credit? This conversation becomes billable again and reappears in the customer's app.`)) {
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await api.uncreditConversation(token, detail.id);
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [detail, token, refresh, t]);

  if (loading) {
    return (
      <div>
        <header className="page-header">
          <h1><Trans>Conversation</Trans></h1>
        </header>
        <p className="muted"><Trans>Loading…</Trans></p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div>
        <header className="page-header">
          <h1><Trans>Conversation</Trans></h1>
          <div className="row-actions">
            <Link to="/conversations" className="ghost"><Trans>Back to list</Trans></Link>
          </div>
        </header>
        <div className="banner err">{loadError}</div>
      </div>
    );
  }

  if (!detail) return null;

  const models = Array.from(
    new Set(detail.turns.map((t) => t.modelDisplayName ?? t.providerModelId)),
  );

  // A turn that never completed leaves no trace in the message thread — the
  // assistant row just stops. The API has carried error_code / error_message all
  // along; without this block they were dropped on the floor. Failed *tool calls*
  // need nothing here: they already render red inline (ContentPart, below).
  // "active" is excluded: a turn in flight is not a failure.
  const failedTurns = detail.turns.filter(
    (turn) => turn.status !== "completed" && turn.status !== "active",
  );

  return (
    <div>
      <header className="page-header">
        <div>
          <div className="muted short-id">
            <Link to="/conversations"><Trans>← All conversations</Trans></Link>
          </div>
          <h1>{detail.title || t`(untitled)`}</h1>
        </div>
        <div className="row-actions">
          {detail.feedback.negativeCount > 0 && (
            <Badge tone="err">
              {`👎 ${detail.feedback.negativeCount}`}
            </Badge>
          )}
          {detail.feedback.positiveCount > 0 && (
            <Badge tone="ok">{`👍 ${detail.feedback.positiveCount}`}</Badge>
          )}
          {detail.archivedAt && <Badge tone="warn">{t`archived`}</Badge>}
          {detail.credit && <Badge tone="ok">{t`credited`}</Badge>}
          {canCredit &&
            (detail.credit ? (
              <button className="ghost danger" disabled={busy} onClick={() => void uncredit()}>
                <Trans>Remove credit</Trans>
              </button>
            ) : (
              <button className="ghost" disabled={busy} onClick={() => setCreditOpen(true)}>
                <Trans>Credit</Trans>
              </button>
            ))}
        </div>
      </header>

      {actionError && <div className="banner err">{actionError}</div>}

      {detail.turns.some(turn => turn.guidance?.requestedMode === "skills-v1" || turn.guidance?.mode === "skills-v1") && (
        <details className="conversation-guidance">
          <summary><Trans>Guidance diagnostics</Trans></summary>
          {detail.turns.filter(turn => turn.guidance?.requestedMode === "skills-v1" || turn.guidance?.mode === "skills-v1").map(turn => (
            <pre key={turn.turnId}>{JSON.stringify({ turnId: turn.turnId, ...turn.guidance }, null, 2)}</pre>
          ))}
        </details>
      )}
      {failedTurns.length > 0 && (
        <section className="banner err conversation-failed-turns">
          <strong>
            <Trans>{failedTurns.length} turn(s) did not complete</Trans>
          </strong>
          <ul>
            {failedTurns.map((turn) => (
              <li key={turn.turnId}>
                <span className="mono">
                  {new Date(turn.startedAt).toLocaleString(i18n.locale)}
                </span>{" "}
                · <Badge tone="err">{turn.status}</Badge>
                {turn.errorCode && <span className="mono"> {turn.errorCode}</span>}
                {turn.errorMessage && <span> — {turn.errorMessage}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="conversation-header">
        <div className="kv">
          <div><strong><Trans>Customer</Trans></strong></div>
          <div>
            <Link to={`/customers/${detail.customerId}`}>{detail.customerName}</Link>
          </div>
          <div><strong><Trans>Machine</Trans></strong></div>
          <div>
            {detail.machineName ??
              (detail.machineId ? detail.machineId.slice(0, 8) + "…" : "—")}
          </div>
          <div><strong><Trans>Created</Trans></strong></div>
          <div className="mono">
            {new Date(detail.createdAt).toLocaleString(i18n.locale)}
          </div>
          <div><strong><Trans>Updated</Trans></strong></div>
          <div className="mono">
            {new Date(detail.updatedAt).toLocaleString(i18n.locale)}
          </div>
          <div><strong><Trans>Models</Trans></strong></div>
          <div>{models.length > 0 ? models.join(", ") : "—"}</div>
        </div>
      </section>

      {detail.credit && (
        <div className="banner ok">
          <Trans>
            Credited by {detail.credit.creditedByEmail ?? "—"} on{" "}
            {new Date(detail.credit.createdAt).toLocaleString(i18n.locale)} —{" "}
            {i18n._(CREDIT_REASON_LABELS[detail.credit.reason])}
          </Trans>
          {detail.credit.note && <div className="muted small">{detail.credit.note}</div>}
          <div className="muted small">
            <Trans>
              This conversation is billed to nobody and is hidden from the customer's app.
            </Trans>
          </div>
        </div>
      )}

      <section className="conversation-totals">
        <div className="conversation-stat">
          <div className="conversation-stat-label"><Trans>Total cost</Trans></div>
          <div className="conversation-stat-value mono">
            {detail.credit ? (
              <>
                <s className="muted">{formatCost(detail.totals.costUsd, i18n.locale)}</s>{" "}
                {formatCost(0, i18n.locale)}
              </>
            ) : (
              formatCost(detail.totals.costUsd, i18n.locale)
            )}
          </div>
        </div>
        <div className="conversation-stat">
          <div className="conversation-stat-label"><Trans>Title cost</Trans></div>
          <div className="conversation-stat-value mono">
            {formatCost(detail.totals.titleCostUsd, i18n.locale)}
          </div>
        </div>
        <div className="conversation-stat">
          <div className="conversation-stat-label"><Trans>Turns</Trans></div>
          <div className="conversation-stat-value mono">
            {formatInt(detail.totals.turnCount, i18n.locale)}
          </div>
        </div>
        <div className="conversation-stat">
          <div className="conversation-stat-label"><Trans>Messages</Trans></div>
          <div className="conversation-stat-value mono">
            {formatInt(detail.totals.messageCount, i18n.locale)}
          </div>
        </div>
        <div className="conversation-stat">
          <div className="conversation-stat-label"><Trans>Input tokens</Trans></div>
          <div className="conversation-stat-value mono">
            {formatInt(detail.totals.tokens.inputTokens, i18n.locale)}
          </div>
        </div>
        <div className="conversation-stat">
          <div className="conversation-stat-label"><Trans>Cached read</Trans></div>
          <div className="conversation-stat-value mono">
            {formatInt(detail.totals.tokens.cachedInputTokens, i18n.locale)}
          </div>
        </div>
        <div className="conversation-stat">
          <div className="conversation-stat-label"><Trans>Cache write</Trans></div>
          <div className="conversation-stat-value mono">
            {formatInt(detail.totals.tokens.cacheWriteTokens, i18n.locale)}
          </div>
        </div>
        <div className="conversation-stat">
          <div className="conversation-stat-label"><Trans>Output tokens</Trans></div>
          <div className="conversation-stat-value mono">
            {formatInt(detail.totals.tokens.outputTokens, i18n.locale)}
          </div>
        </div>
      </section>

      <section className="conversation-pricing">
        <h2><Trans>Provider request pricing</Trans></h2>
        <p className="muted small"><Trans>Recorded customer costs and rates. Input tokens include cache reads and writes. Rates are USD per million tokens.</Trans></p>
        {detail.turns.map((turn, index) => (
          <details key={turn.turnId} className="request-group">
            <summary>
              <Trans>Turn {index + 1}</Trans> · {turn.modelDisplayName ?? turn.providerModelId} · {formatRecordedUsd(turn.costUsd, i18n.locale)}
            </summary>
            <div className="muted small mono">{turn.turnId} · {new Date(turn.startedAt).toLocaleString(i18n.locale)}</div>
            {turn.requests.length === 0 && <p className="muted"><Trans>No recorded requests.</Trans></p>}
            {turn.requests.map((request) => <RequestPricing key={request.id} request={request} />)}
          </details>
        ))}
        {detail.auxiliaryRequests.length > 0 && (
          <details className="request-group">
            <summary><Trans>Requests without a turn (including titles)</Trans> · {formatRecordedUsd(detail.totals.titleCostUsd, i18n.locale)}</summary>
            {detail.auxiliaryRequests.map((request) => <RequestPricing key={request.id} request={request} />)}
          </details>
        )}
      </section>

      <section className="conversation-messages">
        {detail.messages.length === 0 ? (
          <p className="muted"><Trans>This conversation has no messages yet.</Trans></p>
        ) : (
          detail.messages.map((m) => <MessageCard key={m.id} message={m} />)
        )}
      </section>

      {creditOpen && (
        <CreditConversationDialog
          conversationId={detail.id}
          costUsd={detail.totals.costUsd}
          onCancel={() => setCreditOpen(false)}
          onSaved={() => {
            setCreditOpen(false);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

// Credit dialog — same shape as the modals in CustomerDetail (backdrop div +
// stopPropagation, no portal, no focus trap). The reason is mandatory, and the
// note becomes mandatory on "other": the cloud enforces both, this just avoids a
// pointless round-trip.
function CreditConversationDialog({
  conversationId,
  costUsd,
  onCancel,
  onSaved,
}: {
  conversationId: string;
  costUsd: number;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const token = useToken();
  const { t, i18n } = useLingui();
  const [reason, setReason] = useState<ConversationCreditReason>("product_bug");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const noteRequired = reason === "other";

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (noteRequired && !note.trim()) {
      setError(t`A note is required when the reason is "Other".`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.creditConversation(token, conversationId, {
        reason,
        note: note.trim() || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2><Trans>Credit this conversation</Trans></h2>
        <p className="muted small">
          <Trans>
            {formatCost(costUsd, i18n.locale)} stops counting against the customer's
            plan budget and licence cap, and the conversation disappears from the
            customer's app. You can undo this later.
          </Trans>
        </p>
        <form onSubmit={submit}>
          <label>
            <Trans>Reason</Trans>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value as ConversationCreditReason)}
            >
              {CONVERSATION_CREDIT_REASONS.map((r) => (
                <option key={r} value={r}>
                  {i18n._(CREDIT_REASON_LABELS[r])}
                </option>
              ))}
            </select>
          </label>
          <label>
            {noteRequired ? <Trans>Note (required)</Trans> : <Trans>Note (optional)</Trans>}
            <textarea
              rows={3}
              value={note}
              maxLength={CONVERSATION_CREDIT_NOTE_MAX_LENGTH}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t`What happened, and what did we tell the customer?`}
            />
          </label>
          {error && <div className="banner err">{error}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </button>
            <button type="submit" disabled={submitting}>
              {submitting ? <Trans>Crediting…</Trans> : <Trans>Credit</Trans>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MessageCard({ message }: { message: AdminConversationMessage }) {
  const { i18n, t } = useLingui();
  return (
    <article className={`conversation-message role-${message.role}`}>
      <header className="conversation-message-header">
        <div className="conversation-message-role">
          <Badge tone={roleTone(message.role)}>{message.role}</Badge>
          <span className="mono muted">
            {new Date(message.createdAt).toLocaleString(i18n.locale)}
          </span>
          {message.usage?.modelDisplayName && (
            <span className="muted"> · {message.usage.modelDisplayName}</span>
          )}
          {message.truncatedByQuota && (
            <Badge tone="warn">{t`truncated (quota)`}</Badge>
          )}
        </div>
        {message.usage && (
          <div className="conversation-message-usage mono">
            <span title={t`Input tokens`}>
              <Trans>in</Trans> {formatInt(message.usage.tokens.inputTokens, i18n.locale)}
            </span>
            {" · "}
            <span title={t`Cached read`}>
              <Trans>cached</Trans>{" "}
              {formatInt(message.usage.tokens.cachedInputTokens, i18n.locale)}
            </span>
            {" · "}
            <span title={t`Cache write`}>
              <Trans>cache-w</Trans>{" "}
              {formatInt(message.usage.tokens.cacheWriteTokens, i18n.locale)}
            </span>
            {" · "}
            <span title={t`Output tokens`}>
              <Trans>out</Trans>{" "}
              {formatInt(message.usage.tokens.outputTokens, i18n.locale)}
            </span>
            {" · "}
            <strong>{formatCost(message.usage.costUsd, i18n.locale)}</strong>
          </div>
        )}
      </header>
      <div className="conversation-message-body">
        {(message.content?.parts ?? []).map((part, idx) => (
          <ContentPart key={idx} part={part} />
        ))}
      </div>
      {message.feedback && <FeedbackNote feedback={message.feedback} />}
    </article>
  );
}

// What the end user said about this reply, rendered right below it so the
// complaint and the answer that caused it read together.
function FeedbackNote({ feedback }: { feedback: AdminMessageFeedback }) {
  const { i18n, t } = useLingui();
  const negative = feedback.rating === "negative";
  return (
    <div
      className={`conversation-feedback ${negative ? "conversation-feedback-negative" : ""}`}
    >
      <div className="conversation-feedback-header">
        <Badge tone={negative ? "err" : "ok"}>
          {negative ? t`reported` : t`helpful`}
        </Badge>
        {feedback.reason && (
          <strong>{i18n._(FEEDBACK_REASON_LABELS[feedback.reason])}</strong>
        )}
        <span className="mono muted">
          {new Date(feedback.createdAt).toLocaleString(i18n.locale)}
        </span>
      </div>
      {feedback.comment ? (
        <pre className="mono wrap conversation-text">{feedback.comment}</pre>
      ) : (
        negative && (
          <p className="muted">
            <Trans>No comment provided.</Trans>
          </p>
        )
      )}
    </div>
  );
}

function ContentPart({ part }: { part: AdminMessageContentPart }) {
  if (part.type === "text") {
    return <pre className="mono wrap conversation-text">{part.text}</pre>;
  }
  if (part.type === "tool_use") {
    return (
      <CollapsibleTool
        kind="tool_use"
        toneOverride="unknown"
        toolName={part.toolName}
        callId={part.providerCallId}
        body={JSON.stringify(part.arguments, null, 2)}
      />
    );
  }
  if (part.type === "tool_result") {
    const raw =
      typeof part.content === "string"
        ? part.content
        : JSON.stringify(part.content, null, 2);
    return (
      <CollapsibleTool
        kind="tool_result"
        toneOverride={part.isError ? "err" : "ok"}
        toolName={part.toolName}
        callId={part.providerCallId}
        body={raw}
        isError={part.isError}
      />
    );
  }
  return null;
}

function CollapsibleTool({
  kind,
  toneOverride,
  toolName,
  callId,
  body,
  isError,
}: {
  kind: "tool_use" | "tool_result";
  toneOverride: Tone;
  toolName: string;
  callId: string;
  body: string;
  isError?: boolean;
}) {
  const { t } = useLingui();
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`conversation-tool ${isError ? "conversation-tool-error" : ""}`}>
      <button
        type="button"
        className="conversation-tool-header conversation-tool-toggle"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? t`Collapse` : t`Expand`}
      >
        <span className="conversation-message-toggle-icon" aria-hidden="true">
          {expanded ? "▾" : "▸"}
        </span>
        <Badge tone={toneOverride}>{kind}</Badge>
        <span className="mono">{toolName}</span>
        <span className="mono muted short-id">{callId}</span>
      </button>
      {expanded && <pre className="mono wrap">{body}</pre>}
    </div>
  );
}

function roleTone(role: AdminMessageRole): Tone {
  if (role === "user") return "ok";
  if (role === "assistant") return "warn";
  if (role === "tool") return "unknown";
  return "unknown";
}

function formatInt(n: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(Math.round(n));
}
